import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateQRCode, downloadQRCode, generateBrandedQRCode, generateQRCodeWithInstructions } from '../utils/qrCodeGenerator';

const { FiDownload, FiCopy, FiExternalLink, FiRefreshCw, FiSettings, FiPrinter, FiShare2, FiCheck, FiLoader, FiAlertCircle, FiLink, FiInfo } = FiIcons;

const QRCodeDisplay = ({ companyId, companyName, branding = {}, showCustomization = true }) => {
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [qrOptions, setQrOptions] = useState({
    includeLogo: false,
    includeInstructions: true,
    customText: '',
    size: 300
  });

  // Helper to check for logo existence regardless of property name
  const hasLogo = !!(branding.logo || branding.logoUrl);

  // Re-generate when ID changes
  useEffect(() => {
    if (companyId) {
      generateQRCodeImage();
    }
  }, [companyId, qrOptions.includeLogo, qrOptions.includeInstructions, qrOptions.customText, qrOptions.size, branding]);

  const generateQRCodeImage = async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    try {
      let result;
      if (qrOptions.includeInstructions) {
        result = await generateQRCodeWithInstructions(
          companyId, 
          companyName || 'Din Bedrift',
          qrOptions.customText || 'Skann for å varsle trygt'
        );
      } else if (qrOptions.includeLogo && hasLogo) {
        result = await generateBrandedQRCode(companyId, branding, { width: qrOptions.size });
      } else {
        result = await generateQRCode(companyId, { width: qrOptions.size });
      }

      if (result.success) {
        setQrCode(result);
      } else {
        setError("Kunne ikke generere QR-kode");
      }
    } catch (err) {
      setError("En feil oppstod");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!qrCode?.url) return;
    try {
      await navigator.clipboard.writeText(qrCode.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <SafeIcon icon={FiLink} className="text-blue-600 dark:text-blue-400" />
          QR-kode og varslingslenke
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">Dette er den unike inngangen til deres varslingsside.</p>
      </div>

      <div className="p-6 grid lg:grid-cols-2 gap-8">
        {/* Left: QR Preview */}
        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 flex items-center justify-center min-h-[300px] border border-gray-200 dark:border-gray-600">
            {loading ? (
              <SafeIcon icon={FiLoader} className="text-4xl text-blue-600 animate-spin" />
            ) : error ? (
              <div className="text-center text-red-600">
                <SafeIcon icon={FiAlertCircle} className="text-3xl mx-auto mb-2" />
                <p>{error}</p>
              </div>
            ) : qrCode?.dataUrl ? (
              <img src={qrCode.dataUrl} alt="QR Kode" className="max-w-full h-auto rounded shadow-sm" />
            ) : (
              <p className="text-gray-400">Laster QR-kode...</p>
            )}
          </div>

          {/* URL Verification Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <label className="block text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider mb-2">
              Direktelenke (Sjekk at denne virker)
            </label>
            <div className="flex gap-2">
              <input 
                type="text" 
                readOnly 
                value={qrCode?.url || ''} 
                className="flex-1 text-sm font-mono border-blue-300 dark:border-blue-700 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200" 
              />
              <button 
                onClick={() => window.open(qrCode?.url, '_blank')} 
                className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Test lenken i ny fane"
              >
                <SafeIcon icon={FiExternalLink} />
              </button>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
              <SafeIcon icon={FiInfo} /> Lenken skal inneholde <strong>/s/</strong> for å fungere korrekt.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => downloadQRCode(companyId, companyName)} 
              className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiDownload} /> Last ned PNG
            </button>
            <button 
              onClick={copyToClipboard} 
              className="flex items-center justify-center gap-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Kopiert!' : 'Kopier lenke'}
            </button>
          </div>
        </div>

        {/* Right: Settings */}
        {showCustomization && (
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">Tilpasninger</h4>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <input 
                  type="checkbox" 
                  checked={qrOptions.includeInstructions} 
                  onChange={(e) => setQrOptions(prev => ({ ...prev, includeInstructions: e.target.checked }))} 
                  className="w-5 h-5 text-blue-600 rounded dark:bg-gray-700 dark:border-gray-500" 
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white block">Inkluder instruksjoner</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Legger til tekst og ramme rundt koden (Anbefalt for utskrift)</span>
                </div>
              </label>

              {qrOptions.includeInstructions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Instruksjonstekst</label>
                  <input 
                    type="text" 
                    value={qrOptions.customText} 
                    onChange={(e) => setQrOptions(prev => ({ ...prev, customText: e.target.value }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Skann for å varsle"
                  />
                </div>
              )}

              {hasLogo && (
                <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <input 
                    type="checkbox" 
                    checked={qrOptions.includeLogo} 
                    onChange={(e) => setQrOptions(prev => ({ ...prev, includeLogo: e.target.checked }))} 
                    className="w-5 h-5 text-blue-600 rounded dark:bg-gray-700 dark:border-gray-500" 
                  />
                  <span className="font-medium text-gray-900 dark:text-white">Inkluder bedriftslogo</span>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Størrelse</label>
                <select 
                  value={qrOptions.size} 
                  onChange={(e) => setQrOptions(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value={200}>Liten (200px)</option>
                  <option value={300}>Medium (300px)</option>
                  <option value={500}>Stor (500px)</option>
                </select>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
              <h5 className="font-bold text-yellow-900 dark:text-yellow-100 text-sm mb-2 flex items-center gap-2">
                <SafeIcon icon={FiPrinter} /> Tips for utskrift
              </h5>
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                For best resultat, last ned "Stor" versjon med instruksjoner. Heng den opp på steder der ansatte ferdes, som kantine, pauserom eller ved inngangspartiet.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeDisplay;